"use client";

import { Suspense, useMemo } from "react";

import { Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom";

import { AuthGuard } from "@/domain/auth/AuthGuard";
import { LoadingSpinner } from "@/domain/ui/LoadingSpinner";
import { type RouteConfig, routes } from "@/routes/routes";

const PUBLIC_ROUTES = [
  "/errors/unauthorized",
  "/errors/forbidden",
  "/errors/not-found",
  "/errors/internal-server-error",
  "/errors/under-maintenance",
] as const;

function isPublicRoute(path: string): boolean {
  return PUBLIC_ROUTES.some((route) => path.startsWith(route));
}

function renderRoutes(routeConfigs: RouteConfig[]) {
  return routeConfigs.map((route, index) => {
    const isPublic = isPublicRoute(route.path);
    const element = isPublic ? route.element : <AuthGuard>{route.element}</AuthGuard>;

    return (
      <Route
        key={route.path + index}
        path={route.path}
        element={<Suspense fallback={<LoadingSpinner />}>{element}</Suspense>}
      >
        {route.children && renderRoutes(route.children)}
      </Route>
    );
  });
}

interface AppRouterProps {
  basename: string;
}

export function AppRouter({ basename }: AppRouterProps) {
  const router = useMemo(() => {
    const routeObjects = createRoutesFromElements(<>{renderRoutes(routes)}</>);

    return createBrowserRouter(routeObjects, basename ? { basename } : undefined);
  }, [basename]);

  return <RouterProvider router={router} />;
}
